import { Platform } from "react-native";

type LegacyAutocompletePrediction = {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text?: string;
  };
};

type LegacyAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

type LegacyPlaceResult = {
  place_id?: string;
  formatted_address?: string;
  address_components?: LegacyAddressComponent[];
  geometry?: {
    location?: {
      lat: () => number;
      lng: () => number;
    };
  };
};

type LegacyPlacesLibrary = {
  AutocompleteService: new () => {
    getPlacePredictions: (
      request: { input: string; types?: string[] },
      callback: (
        predictions: LegacyAutocompletePrediction[] | null,
        status: string,
      ) => void,
    ) => void;
  };
  PlacesService: new (container: HTMLElement) => {
    getDetails: (
      request: { placeId: string; fields: string[] },
      callback: (place: LegacyPlaceResult | null, status: string) => void,
    ) => void;
  };
  PlacesServiceStatus: {
    OK: string;
  };
};

type NewAutocompletePrediction = {
  placeId?: string;
  text?: { text?: string };
  structuredFormat?: {
    mainText?: { text?: string };
    secondaryText?: { text?: string };
  };
};

type NewAutocompleteResponse = {
  suggestions?: { placePrediction?: NewAutocompletePrediction }[];
};

type NewAddressComponent = {
  longText?: string;
  shortText?: string;
  types?: string[];
};

type NewPlaceDetailsResponse = {
  id?: string;
  formattedAddress?: string;
  addressComponents?: NewAddressComponent[];
  location?: {
    latitude?: number;
    longitude?: number;
  };
};

type LegacyAutocompleteResponse = {
  status?: string;
  predictions?: LegacyAutocompletePrediction[];
};

type LegacyPlaceDetailsResponse = {
  status?: string;
  result?: {
    place_id?: string;
    formatted_address?: string;
    address_components?: LegacyAddressComponent[];
    geometry?: {
      location?: {
        lat?: number;
        lng?: number;
      };
    };
  };
};

// Type declarations for the legacy Google Maps JavaScript fallback.
declare global {
  interface Window {
    google?: {
      maps?: {
        places?: LegacyPlacesLibrary;
      };
    };
  }
}

export type PlacePrediction = {
  placeId: string;
  description: string;
  structuredFormatting: {
    mainText: string;
    secondaryText: string;
  };
};

export type PlaceDetails = {
  placeId: string;
  formattedAddress: string;
  addressComponents: {
    longName: string;
    shortName: string;
    types: string[];
  }[];
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
};

function toPlacePrediction(
  prediction: NewAutocompletePrediction,
): PlacePrediction | null {
  const placeId = prediction.placeId;
  const description = prediction.text?.text;
  if (!placeId || !description) return null;

  return {
    placeId,
    description,
    structuredFormatting: {
      mainText: prediction.structuredFormat?.mainText?.text || description,
      secondaryText: prediction.structuredFormat?.secondaryText?.text || "",
    },
  };
}

function toPlacePredictionFromLegacy(
  prediction: LegacyAutocompletePrediction,
): PlacePrediction {
  return {
    placeId: prediction.place_id,
    description: prediction.description,
    structuredFormatting: {
      mainText: prediction.structured_formatting.main_text,
      secondaryText: prediction.structured_formatting.secondary_text || "",
    },
  };
}

function toAddressComponents(
  components: NewAddressComponent[] | undefined,
): PlaceDetails["addressComponents"] {
  return (components || []).map((component) => ({
    longName: component.longText || "",
    shortName: component.shortText || "",
    types: component.types || [],
  }));
}

function toAddressComponentsFromLegacy(
  components: LegacyAddressComponent[] | undefined,
): PlaceDetails["addressComponents"] {
  return (components || []).map((component) => ({
    longName: component.long_name,
    shortName: component.short_name,
    types: component.types,
  }));
}

function toPlaceDetails(
  place: NewPlaceDetailsResponse,
  fallbackPlaceId: string,
): PlaceDetails {
  return {
    placeId: place.id || fallbackPlaceId,
    formattedAddress: place.formattedAddress || "",
    addressComponents: toAddressComponents(place.addressComponents),
    geometry: {
      location: {
        lat: place.location?.latitude || 0,
        lng: place.location?.longitude || 0,
      },
    },
  };
}

function toPlaceDetailsFromLegacy(
  place: LegacyPlaceResult,
  fallbackPlaceId: string,
): PlaceDetails | null {
  const location = place.geometry?.location;
  if (!location) return null;

  return {
    placeId: place.place_id || fallbackPlaceId,
    formattedAddress: place.formatted_address || "",
    addressComponents: toAddressComponentsFromLegacy(place.address_components),
    geometry: {
      location: {
        lat: location.lat(),
        lng: location.lng(),
      },
    },
  };
}

function toPlaceDetailsFromLegacyRest(
  place: NonNullable<LegacyPlaceDetailsResponse["result"]>,
  fallbackPlaceId: string,
): PlaceDetails | null {
  const location = place.geometry?.location;
  if (typeof location?.lat !== "number" || typeof location.lng !== "number") {
    return null;
  }

  return {
    placeId: place.place_id || fallbackPlaceId,
    formattedAddress: place.formatted_address || "",
    addressComponents: toAddressComponentsFromLegacy(place.address_components),
    geometry: {
      location: {
        lat: location.lat,
        lng: location.lng,
      },
    },
  };
}

type LatLng = { latitude: number; longitude: number };

type SearchPlacesOptions = {
  /** Restrict results to cities when the caller needs an unambiguous timezone. */
  citiesOnly?: boolean;
  /**
   * Restrict results to Google's `(regions)` collection: cities, sublocalities,
   * postal codes, states/provinces, and countries. Excludes businesses,
   * landmarks, and street addresses, but (unlike `citiesOnly`) allows picking a
   * whole state or country. `citiesOnly` wins if both are set.
   */
  regionsOnly?: boolean;
  /**
   * Restrict results to Google's `geocode` type: street addresses, routes,
   * cities, regions, and countries. Excludes businesses and points of
   * interest while keeping every level of place a person might call home.
   * `citiesOnly` and `regionsOnly` win if set.
   */
  addressesOnly?: boolean;
  /**
   * Prioritize results near this point without hiding far-away ones (bias, not
   * restriction). We pass the user's known coordinates so, for example, typing
   * "San" from San Francisco surfaces nearby San * cities first. Omitting it
   * falls back to Google's default IP-based biasing.
   */
  locationBias?: LatLng & { radiusMeters?: number };
  /** Reference point for distance-based ranking of predictions. */
  origin?: LatLng;
};

/**
 * Load Google Places Autocomplete script for web
 */
function loadGooglePlacesScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (Platform.OS !== "web") {
      resolve();
      return;
    }

    // Check if script is already loaded
    if (window.google?.maps?.places) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Google Places script"));
    document.head.appendChild(script);
  });
}

/**
 * Search for places using Google Places Autocomplete API
 * For web: Uses Google Places Autocomplete service (no CORS issues)
 * For native: Calls the API directly (no CORS restrictions)
 */
export async function searchPlaces(
  input: string,
  {
    citiesOnly = false,
    regionsOnly = false,
    addressesOnly = false,
    locationBias,
    origin,
  }: SearchPlacesOptions = {},
): Promise<PlacePrediction[]> {
  const apiKey = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY;
  if (!apiKey || !input.trim()) {
    return [];
  }
  const primaryTypes = citiesOnly
    ? ["(cities)"]
    : regionsOnly
      ? ["(regions)"]
      : addressesOnly
        ? ["geocode"]
        : null;

  try {
    // Use new Places API (New) for both web and native
    // This API supports both cities and full addresses
    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places:autocomplete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": apiKey || "",
            "X-Goog-FieldMask":
              "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
          },
          body: JSON.stringify({
            input: input,
            includedRegionCodes: [], // Empty = all regions
            ...(primaryTypes ? { includedPrimaryTypes: primaryTypes } : {}),
            ...(locationBias
              ? {
                  locationBias: {
                    circle: {
                      center: {
                        latitude: locationBias.latitude,
                        longitude: locationBias.longitude,
                      },
                      // Places circle radius is capped at 50km; a wide bias
                      // still surfaces the whole country, just ranked local-first.
                      radius: locationBias.radiusMeters ?? 50000,
                    },
                  },
                }
              : {}),
            ...(origin
              ? {
                  origin: {
                    latitude: origin.latitude,
                    longitude: origin.longitude,
                  },
                }
              : {}),
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Places API error: ${response.status}`);
      }

      const data = (await response.json()) as NewAutocompleteResponse;
      return (data.suggestions || []).flatMap((suggestion) => {
        const prediction = suggestion.placePrediction;
        if (!prediction) return [];

        const formatted = toPlacePrediction(prediction);
        return formatted ? [formatted] : [];
      });
    } catch (error) {
      console.error(
        "Error using new Places API, falling back to legacy:",
        error,
      );

      // Fallback to legacy API for web only (uses JavaScript library)
      if (Platform.OS === "web") {
        try {
          await loadGooglePlacesScript();
          const places = window.google?.maps?.places;
          if (!places) {
            return [];
          }

          return new Promise<PlacePrediction[]>((resolve) => {
            const service = new places.AutocompleteService();
            service.getPlacePredictions(
              {
                input,
                ...(primaryTypes ? { types: primaryTypes } : {}),
              },
              (predictions, status) => {
                if (status === places.PlacesServiceStatus.OK && predictions) {
                  resolve(predictions.map(toPlacePredictionFromLegacy));
                } else {
                  resolve([]);
                }
              },
            );
          });
        } catch (legacyError) {
          console.error("Legacy API also failed:", legacyError);
          return [];
        }
      } else {
        // Native fallback to legacy REST API
        const typesParam = primaryTypes
          ? `&types=${encodeURIComponent(primaryTypes[0]!)}`
          : "";
        const biasParam = locationBias
          ? `&location=${locationBias.latitude},${locationBias.longitude}&radius=${locationBias.radiusMeters ?? 50000}`
          : "";
        const originParam = origin
          ? `&origin=${origin.latitude},${origin.longitude}`
          : "";
        const url = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(input)}&key=${apiKey}${typesParam}${biasParam}${originParam}`;
        const response = await fetch(url);
        const data = (await response.json()) as LegacyAutocompleteResponse;

        if (data.status === "OK" && data.predictions) {
          return data.predictions.map(toPlacePredictionFromLegacy);
        }
        return [];
      }
    }
  } catch (error) {
    console.error("Error searching places:", error);
    return [];
  }
}

/**
 * Get place details including address components
 */
export async function getPlaceDetails(
  placeId: string,
): Promise<PlaceDetails | null> {
  const apiKey = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return null;
  }

  try {
    if (Platform.OS === "web") {
      try {
        const response = await fetch(
          `https://places.googleapis.com/v1/places/${placeId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": apiKey || "",
              "X-Goog-FieldMask":
                "id,formattedAddress,addressComponents,location",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Places API error: ${response.status}`);
        }

        const data = (await response.json()) as NewPlaceDetailsResponse;
        if (data) {
          return toPlaceDetails(data, placeId);
        }
        return null;
      } catch (error) {
        console.error(
          "Error using new Places API, falling back to legacy:",
          error,
        );
        // Fallback to legacy API
        await loadGooglePlacesScript();
        const places = window.google?.maps?.places;
        if (!places) {
          return null;
        }

        return new Promise<PlaceDetails | null>((resolve) => {
          const service = new places.PlacesService(
            document.createElement("div"),
          );
          service.getDetails(
            {
              placeId,
              fields: ["formatted_address", "address_components", "geometry"],
            },
            (place, status) => {
              if (status === places.PlacesServiceStatus.OK && place) {
                resolve(toPlaceDetailsFromLegacy(place, placeId));
              } else {
                resolve(null);
              }
            },
          );
        });
      }
    } else {
      // Native platforms - use new Places API (New)
      try {
        const response = await fetch(
          `https://places.googleapis.com/v1/places/${placeId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": apiKey || "",
              "X-Goog-FieldMask":
                "id,formattedAddress,addressComponents,location",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Places API error: ${response.status}`);
        }

        const data = (await response.json()) as NewPlaceDetailsResponse;
        if (data) {
          return toPlaceDetails(data, placeId);
        }
        return null;
      } catch (error) {
        console.error(
          "Error using new Places API, falling back to legacy:",
          error,
        );
        // Fallback to legacy API
        const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&key=${apiKey}&fields=formatted_address,address_components,geometry`;
        const response = await fetch(url);
        const data = (await response.json()) as LegacyPlaceDetailsResponse;

        if (data.status === "OK" && data.result) {
          return toPlaceDetailsFromLegacyRest(data.result, placeId);
        }
        return null;
      }
    }
  } catch (error) {
    console.error("Error getting place details:", error);
    return null;
  }
}

/**
 * Extract city and country from place details
 */
export function extractCityAndCountry(
  placeDetails: PlaceDetails,
): { city: string; country: string } | null {
  let city: string | null = null;
  let country: string | null = null;

  for (const component of placeDetails.addressComponents) {
    if (component.types.includes("locality")) {
      city = component.longName;
    } else if (component.types.includes("administrative_area_level_1")) {
      // State/province - use as fallback if no city
      if (!city) {
        city = component.longName;
      }
    } else if (component.types.includes("country")) {
      country = component.shortName; // Use short code (e.g., "US")
    }
  }

  if (city && country) {
    return { city, country };
  }

  return null;
}

export type PlaceArea = {
  /** Coarsest administrative level the selected place resolves to. */
  granularity: "city" | "state" | "country";
  city: string | null;
  /** State/province short code (e.g., "CA") when present. */
  stateCode: string | null;
  stateName: string | null;
  /** ISO country short code (e.g., "US"). */
  countryCode: string;
  /** Country display name (e.g., "United States"). */
  countryName: string;
  latitude: number | null;
  longitude: number | null;
  /** Human label for the place the person actually picked. */
  label: string;
};

/**
 * Extract the geographic area of a selected place, tolerating city-, state-,
 * and country-level picks (the `(regions)` autocomplete collection returns all
 * three). Returns null only when there is no country, which the Places API
 * always provides for a real place.
 */
export function extractPlaceArea(placeDetails: PlaceDetails): PlaceArea | null {
  let city: string | null = null;
  let stateCode: string | null = null;
  let stateName: string | null = null;
  let countryCode: string | null = null;
  let countryName: string | null = null;

  for (const component of placeDetails.addressComponents) {
    if (component.types.includes("locality")) {
      city = component.longName;
    } else if (
      !city &&
      component.types.includes("administrative_area_level_3")
    ) {
      // Some smaller towns only surface at this level.
      city = component.longName;
    } else if (component.types.includes("administrative_area_level_1")) {
      stateCode = component.shortName;
      stateName = component.longName;
    } else if (component.types.includes("country")) {
      countryCode = component.shortName;
      countryName = component.longName;
    }
  }

  if (!countryCode) return null;

  const granularity: PlaceArea["granularity"] = city
    ? "city"
    : stateCode
      ? "state"
      : "country";
  const label = city
    ? stateCode
      ? `${city}, ${stateCode}`
      : city
    : granularity === "state"
      ? (stateName ?? stateCode ?? countryName ?? countryCode)
      : (countryName ?? countryCode);

  return {
    granularity,
    city,
    stateCode,
    stateName,
    countryCode,
    countryName: countryName ?? countryCode,
    latitude: placeDetails.geometry.location.lat || null,
    longitude: placeDetails.geometry.location.lng || null,
    label,
  };
}
